<template>
  <div class="container-nzidoo">
    <page-title title="sentence.offerandauctionsmanagment" />

    <div class="tabs">
      <button class="btn-tab" :class="{ active: activeTab === 'all' }" @click="activeTab = 'all'">
        {{ $t('filter.all') }}
      </button>
      <button class="btn-tab" :class="{ active: activeTab === 'offers' }" @click="activeTab = 'offers'">
        {{ $t('sidebar.myOffres') }}
      </button>
      <button class="btn-tab" :class="{ active: activeTab === 'bids' }" @click="activeTab = 'bids'">
        {{ $t('sidebar.myAuctions') }}
      </button>
    </div>

    <div class="filters scroll-horizontal">
      <template v-if="activeTab === 'offers'">
        <button v-for="f in offersFilters" :key="f.key" class="btn-tab"
          :class="{ active: offersFilterStatus === f.key }" @click="offersFilterStatus = f.key">
          {{ f.label }} 
        </button>
      </template>
    </div>

    <div v-if="!loader" class="tab-content">
      <div v-if="activeTab === 'all'" class="tab-panel">
        <div v-if="allItems.length === 0" class="empty">
          {{ $t('sentence.noItemsFound') }}
        </div>
        <div v-else class="row px-2 px-md-3" ref="allProductList">
          <div v-for="item in allItems" :key="`${item.type}-${item.id}`" class="col-md-4 p-1 px-0 px-md-1">
            <ProductCard v-if="item.type === 'offer'" :user="item.product?.user || item?.user" :product="item.product"
              :offer="item" :order="item" :pictures="item.product?.pictures" :showBtn="false"
              @show="showOfferIdPage(item.product)" @make-offer="openOfferModal" />
            <ProductCard v-else :pictures="item.product.pictures" :user="item.product.user" :product="item.product"
              :showBtn="false" :showStats=false :showBoost=false @show="showAuctionIdPage(item.product)"
              @make-auction="openAuctionModal" />
          </div>
        </div>
      </div>
      <div v-else-if="activeTab === 'offers'" class="tab-panel">
        <div v-if="filteredOffers.length === 0" class="empty">
          {{ $t('sentence.noOfferFound') }}
        </div>
        <div v-else class="row px-2 px-md-3" ref="offersProductList">
          <div v-for="item in filteredOffers" :key="item.id" class="col-md-4 p-1 px-0 px-md-1">
            <ProductCard :user="item.product?.user || item?.user" :product="item.product" :offer="item" :order="item"
              :pictures="item.product?.pictures" :showBtn="false" @show="showOfferIdPage(item.product)"
              @make-offer="openOfferModal" />
          </div>
        </div>
      </div>

      <div v-else class="tab-panel">
        <div v-if="filteredBids.length === 0" class="empty">
          {{ $t('sentence.noBidFound') }}
        </div>
        <div v-else class="row px-2 px-md-3" ref="bidsProductList">
          <div v-for="item in filteredBids" :key="item.product.id" class="col-md-4 p-1 px-0 px-md-1">
            <ProductCard :pictures="item.product.pictures" :user="item.product.user" :product="item.product"
              :showBtn="false" :showStats=false :showBoost=false @show="showAuctionIdPage(item.product)"
              @make-auction="openAuctionModal" />
          </div>
        </div>
      </div>
    </div>
    <LoadingSpinner v-else />

    <ProductDetailsModal :inSaleProduct="selectedProduct" />

    <MakeAnOfferModal v-if="offerProduct" :products="offerProduct" @offer-sent="onOfferSentFromModal" />
    <MakeAnAuctionModal v-if="auctionProduct" :product="auctionProduct" @bid-sent="onBidSentFromModal" />
    <login-modal v-if="$route" :route="$route.name" />

    <notifications class="custom-notifications mt-2" group="success" position="top center" />
    <notifications class="custom-notifications mt-2" group="error" position="top center" />
    <v-dialog />

    <RatingModal :ratingModel="ratingModal" :ratingModelId="ratingModalId" :modelToRates="modalToRate"
      :modelToRatesId="modalToRateId" />
    <v-dialog />
  </div>
</template>

<script>
import config from "@/config.js";
import ProductDetailsModal from "@/components/modals/ProductDetailsModal";
import RatingModal from "@/components/modals/RatingModal";
import ProductCard from "@/components/templates/ProductCard.vue";
import gsapAnimations from "@/services/gsapAnimations";
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import PageTitle from '@/components/templates/PageTitle.vue';
import MakeAnAuctionModal from "@/components/modals/MakeAnAuctionModal.vue";
import MakeAnOfferModal from "@/components/modals/MakeAnOfferModal.vue";
import LoginModal from '@/components/modals/LoginModal.vue';

export default {
  name: "OffersBidsList",
  components: {
    ProductDetailsModal,
    RatingModal,
    ProductCard,
    LoadingSpinner,
    MakeAnOfferModal,
    MakeAnAuctionModal,
    LoginModal,
    PageTitle
  },

  data() {
    return {
      storageUrl: config.storageUrl,
      offers: [],
      bids: [],
      selectedProduct: null,
      auctionProduct: null,
      offerProduct: null,
      activeTab: "all",
      offersFilterStatus: "all",
      bidsFilterStatus: "all",
      ratingModal: "",
      modalToRate: "",
      ratingModalId: -1,
      modalToRateId: -1,
      loader: false,
    };
  },

  async mounted() {
    this.loader=true;
    this.$nextTick(() => {
      this.initAnimations();
    });
    await this.getUserOffers();
    await this.getUserBids();
    this.loader=false;
  },

  watch: {
    allItems(newVal) {
      if (newVal.length>0&&this.activeTab==='all') {
        this.$nextTick(() => {
          const container=this.$refs.allProductList;
          if (container) {
            gsapAnimations.animateNewItems(container, ".product-card");
          }
        });
      }
    },
    filteredOffers(newVal) {
      if (newVal.length>0&&this.activeTab==='offers') {
        this.$nextTick(() => {
          const container=this.$refs.offersProductList;
          if (container) {
            gsapAnimations.animateNewItems(container, ".product-card");
          }
        });
      }
    },
    filteredBids(newVal) {
      if (newVal.length>0&&this.activeTab==='bids') {
        this.$nextTick(() => {
          const container=this.$refs.bidsProductList;
          if (container) {
            gsapAnimations.animateNewItems(container, ".product-card");
          }
        });
      }
    },
    activeTab() {
      this.$nextTick(() => {
        this.initAnimations();
      });
    },
  },

  computed: {
    allItems() {
      const offersList=Array.isArray(this.offers)? this.offers.map(o => ({ ...o, type: 'offer' })):[];
      const bidsList=Array.isArray(this.bids)? this.bids.map(b => ({ ...b, type: 'bid' })):[];
      const combined=[...offersList, ...bidsList];
      combined.sort((a, b) => new Date(b.created_at)-new Date(a.created_at));
      return combined;
    },
    offersFilters() {
      return [
        { key: "all", label: this.$t("filter.all") },
        { key: "waiting", label: this.$t("action.waiting") },
        { key: "accepted", label: this.$t("action.accepted") },
        // { key: "sold", label: this.$t("action.sold") },
        { key: "refused", label: this.$t("action.refused") },
      ];
    },
    bidsFilters() {
      return [
        { key: "all", label: this.$t("filter.all") },
        { key: "highest", label: this.$t("sentence.currentBidIsTheHighest") },
        { key: "won", label: this.$t("sentence.offerWonAuction") },
        { key: "overridden", label: this.$t("sentence.offerOverriden") },
      ];
    },
    filteredOffers() {
      const list=Array.isArray(this.offers)? this.offers.slice():[];
      switch (this.offersFilterStatus) {

        case "waiting":
          return list.filter((p) => p.status===null);
        case "accepted":
          return list.filter((p) => p.status===1);
        case "sold":
          return list.filter((p) => p.product.has_been_sold===1);
        case "refused":
          return list.filter((p) => p.status===0);
        default:
          return list;
      }
    },
    filteredBids() {
      const list=Array.isArray(this.bids)? this.bids.slice():[];
      switch (this.bidsFilterStatus) {
        case "highest":
          return list.filter((b) => b.status===null&&b.id==b.product[0].bids[0].id);
        case "won":
          return list.filter((b) => b.status===true);
        case "overridden":
          return list.filter((b) => b.status===null&&b.id!=b.product[0].bids[0].id);
        default:
          return list;
      }
    },
  },

  methods: {
    show(modalName) {
      this.$modal.show(modalName);
    },
    openOfferModal(product) {
      // Close modal first if it's open
      this.$modal.hide('make-offer');

      this.offerProduct=null;

      // Set new product data in next tick
      this.$nextTick(() => {
        this.offerProduct=product;
        this.$nextTick(() => {
          this.$modal.show('make-offer');
        });
      });
    },
    openAuctionModal(product) {
      // Close modal first if it's open
      this.$modal.hide('make-auction');

      this.auctionProduct=null;

      // Set new product data in next tick
      this.$nextTick(() => {
        this.auctionProduct=product;
        this.$nextTick(() => {
          this.$modal.show('make-auction');
        });
      });
    },
    async getUserOffers() {
      try {
        const grouped=(await this.$axios.get("user/offers")).data;
        const result=[];
        Object.keys(grouped).forEach(productId => {
          const group=grouped[productId];
          if (!group||Object.keys(group).length===0) return;
          const bestOffer=group.best_offer;
          const offers=Object.keys(group)
            .filter(key => key!=="best_offer")
            .map(key => group[key]);
          bestOffer.nbOffers=offers.length;
          result.push(bestOffer);
        });
        result.sort((a, b) => new Date(b.created_at)-new Date(a.created_at));
        this.offers=result;
      } catch (err) {
        console.error("Error fetching user offers:", err.response?.data||err);
      }
    },
    async getUserBids() {
      try {
        const response=await this.$axios.get("user/bids");
        const grouped=response.data;
        const result=[];
        Object.keys(grouped).forEach(productId => {
          const bids=grouped[productId];
          if (!bids||bids.length===0) return;
          const highestBid=bids[0];
          highestBid.nbOffers=bids.length;
          result.push(highestBid);
        });
        result.sort((a, b) => new Date(b.created_at)-new Date(a.created_at));
        this.bids=result;
      } catch (err) {
        console.error("Error fetching user bids:", err.response?.data||err);
      }
    },

    initAnimations() {
      gsapAnimations.killAllScrollTriggers();
      this.$nextTick(() => {
        let containerRef;
        if (this.activeTab==='all') {
          containerRef=this.$refs.allProductList;
        } else if (this.activeTab==='offers') {
          containerRef=this.$refs.offersProductList;
        } else {
          containerRef=this.$refs.bidsProductList;
        }
        if (containerRef) {
          gsapAnimations.setupScrollAnimation(containerRef, ".product-card");
        }
      });
    },
    showAuctionIdPage(product) {
      this.$router.push({ name: "productDetails", params: { id: product.id } });
    },
    showOfferIdPage(product) {
      this.$router.push({ name: "productDetails", params: { id: product.id } });
    },
    async onOfferSentFromModal() {
      try {
        await this.getUserOffers();
      } catch (err) {
        console.error('Failed to refresh product after offer:', err);
      }
    },
    async onBidSentFromModal() {
      try {
        await this.getUserBids();
      } catch (err) {
        console.error('Failed to refresh product after bid:', err);
      }
    },
  },

  beforeDestroy() {
    gsapAnimations.killAllScrollTriggers();
  }
};
</script>
<style scoped>
.scroll-horizontal {
  overflow-x: auto;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

/* ======= PRODUCT LIST (Retained from OffersAuctions) ======= */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ======= EMPTY STATE ======= */
.empty {
  text-align: center;
  padding: 40px 0;
  color: #7a88a6;
  font-size: 0.95rem;
}

/* ======= RESPONSIVE ======= */
@media (max-width: 640px) {
  .container-nzidoo {
    margin: 10px auto;
    padding: 14px;
  }

  /* .tab-button {
    font-size: 0.95rem;
    padding: 9px 12px;
  } */
}


@media (max-width: 320px) {}
</style>
